import { Component, Input, OnInit, Output, EventEmitter, AfterViewInit, OnDestroy} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbDate, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfigDto } from 'src/app/services/dtos';
import { HttpConfigService } from 'src/app/services/http-config.service';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';
import { ServiceSelectionConfirmDialogComponent } from '../../../service-selection-confirm-dialog/service-selection-confirm-dialog.component';
import { PreregisteredDialogComponent } from 'src/app/preregistered-dialog/preregistered-dialog.component';

export interface IdType {
	value: string;
	regex: string;
	maxLength: number;
	desc: string;
}

@Component({
	selector: 'app-service-selection',
	templateUrl: './service-selection.component.html',
	styleUrls: ['./service-selection.component.scss']
})
export class ServiceSelectionComponent implements OnInit, AfterViewInit, OnDestroy {

	environment = environment;
	config: ConfigDto;

	email: string;
	idType: IdType;
	idNumber: string;
	dontHaveId: boolean = false;
	name: string;
	surname: string;
	gender: string;
	genderList = ['Masculino', 'Feminino'];
	dateOfBirth: NgbDate;
	minDob: NgbDate;
	maxDob: NgbDate;
	province: string;
	municipality: string;
	commune: string;
	cap: string;
	capCode: string;

	otpMethod : 'sms' | 'email' = 'sms';
	msisdn: string;

	startDate: any;

	profileType: string = "INDIVIDUAL";
	recordAction: string = "NEW";
	contractType: string = "";

	invalidCap = false;

	idTypeList: IdType[]= [];

	@Output() onNext = new EventEmitter();
	@Output() onBack = new EventEmitter();

	private langChangeSub: Subscription;
	invalidCapText: string;
	invalidEmailMsisdnText: string;
	invalidIdNumberText: string;

	preregistered: boolean = true;
	nameDisabled: boolean = true;
	surnameDisabled: boolean = true;

	constructor(
		private httpConfigService: HttpConfigService, 
		private spinner: NgxSpinnerService, 
		private toast: ToastService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		private modalService: NgbModal,) {
    }

	ngOnInit(): void {

		this.setTranslatedText();
		this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
			this.setTranslatedText();
		});

		const capId = Number(this.route.snapshot.queryParamMap.get('capId'));
		if (isNaN(capId)) {
			this.toast.show(this.invalidCapText);
			this.invalidCap = true;
			return;
		}
		this.httpConfigService.getCapInfo(capId)
		.pipe(
				catchError(error => {
					this.spinner.hide();
					console.log('Error', error);
					
					this.toast.show('Failed fetching CAP data: ' + error.error.message);
					this.invalidCap = true;
					throw error;
				})
			)
			.subscribe(
				data => {
					this.spinner.hide();
					console.log('Got cap data: ', data);
					if (data) {
						this.province = data.province;
						this.municipality = data.municipality;
						this.commune = data.municipality;
						this.cap = data.cap;
						this.capCode = data.capCode;
						this.invalidCap = false;

						this.onPreregistered();
					} else {
						this.invalidCap = true;
						this.toast.show(this.invalidCapText);
					}
				}
			);
	}

	ngAfterViewInit(): void {
		this.getConfig();
		this.configureDateField();
	}

	private configureDateField() {
		let date: Date = new Date();
		date.setFullYear(date.getFullYear() - 30);
		this.startDate = {
			"year": date.getFullYear(),
			"month": date.getMonth()
		}

		date = new Date();
		this.maxDob = new NgbDate(date.getFullYear() - 18, date.getMonth() + 1, date.getDate());

		date.setFullYear(date.getFullYear() - 100);
		this.minDob = new NgbDate(date.getFullYear(), 1, 1);
	}

	getConfig() {
		this.spinner.show();

		this.httpConfigService.getConfig()
		.pipe(
			catchError(error => {
				this.spinner.hide();
				console.log('Error', error);

				this.toast.show('Failed to connect to server');
				throw error;
			})
		)
		.subscribe(
			data => {
				this.spinner.hide();
				if (!data) {
					console.log('Error', data);
					this.toast.show('Error downloading configuration data');
				} else {
					this.config = data;
					console.log(this.config);
				}
			}
		);
	}

	previousStep() {
		this.onBack.emit();
	}

	nextStep() {
		if (this.invalidCap === true) {
			this.toast.show(this.invalidCapText);
			return;
		}

		var dob = undefined;
		if (this.dateOfBirth) {
			dob = new Date();
			dob.setFullYear(this.dateOfBirth.year);
			dob.setMonth(this.dateOfBirth.month - 1);
			dob.setDate(this.dateOfBirth.day);
		}

		const serviceSelection = {
			email: this.email,
			msisdn: this.msisdn,
			dontHaveId: this.dontHaveId,
			idType: this.idType?.value,
			idNumber: this.idNumber,
			name: this.name,
			surname: this.surname,
			gender: this.gender,
			dateOfBirth: dob,
			province: this.province,
			municipality: this.municipality,
			commune: this.commune,
			cap: this.cap,
			profileType: this.profileType,
			recordAction: this.recordAction,
			contractType: this.contractType,
		}

		if ((serviceSelection.email === null || serviceSelection.email === undefined) && (serviceSelection.msisdn === null || serviceSelection.msisdn === undefined)) {
			this.toast.show(this.invalidEmailMsisdnText);
			return;
		}

		if (this.dontHaveId === false && serviceSelection.idNumber === undefined) {
			this.toast.show(this.invalidIdNumberText);
			return;
		}

		const modalRef = this.modalService.open(ServiceSelectionConfirmDialogComponent, { windowClass: "modal-confirm-action", size: 'lg' });
		modalRef.componentInstance.serviceSelection = serviceSelection;
		modalRef.result.then(item => {
			if (item === true) {
				this.onNext.emit({ 'serviceSelection': serviceSelection });
			}
		});

		
	}

	private setTranslatedText() {

		var idType1: string;
		this.translate.get('ID_TYPE_1_DESC').subscribe(value => {
			idType1 = value;
		});
		var idType2: string;
		this.translate.get('ID_TYPE_2_DESC').subscribe(value => {
			idType2 = value;
		});
		var idType3: string;
		this.translate.get('ID_TYPE_3_DESC').subscribe(value => {
			idType3 = value;
		});

		this.idTypeList = [];
		this.idTypeList.push({value: "BI Nacional", regex: "[A-Za-z0-9]{14}", maxLength: 14, desc: idType1});
		this.idTypeList.push({value: "Cartão de Munícipe", regex: "\\d{19}", maxLength: 19, desc: idType2});
		this.idTypeList.push({value: "Passaporte", regex: "[A-Za-z0-9]{9}", maxLength: 9, desc: idType3});
		this.idType = this.idTypeList[0];

		this.translate.get('INVALID_CAP').subscribe(value => {
			this.invalidCapText = value;
		});
		this.translate.get('INVALID_EMAIL_MSISDN').subscribe(value => {
			this.invalidEmailMsisdnText = value;
		});
		this.translate.get('INVALID_ID_NUMBER').subscribe(value => {
			this.invalidIdNumberText = value;
		});
	}

	onDontHaveIdChange() {
		if (this.dontHaveId) {
			// Checkbox is checked — user *doesn't* have ID
			this.idType = undefined;
			this.idNumber = undefined;
		}
	}

	onPreregistered() {
		const modalRef = this.modalService.open(PreregisteredDialogComponent, { windowClass: "modal-preregistered", size: 'lg', backdrop: 'static', keyboard: false});
		modalRef.componentInstance.capCode = this.capCode;
		modalRef.result.then(item => {
			if (item !== undefined) {
				this.name = item.firstName;
				this.surname = item.surname;
			} 
			// else {
			// 	this.preregistered = false;
			// 	this.nameDisabled = false;
			// 	this.surnameDisabled = false;
			// 	this.name = undefined;
			// 	this.surname = undefined;
			// }
		});
	}

	onOtpMethodChange() {
		if (this.otpMethod === 'sms') {
			// SMS selected
			this.email = undefined;
		} else if (this.otpMethod === 'email') {
			// Email selected
			this.msisdn = undefined;
		}
	}

	ngOnDestroy(): void {
    	this.langChangeSub?.unsubscribe();
  	}
}
